import { zodResolver } from "@hookform/resolvers/zod"
import { Button } from "design-system/atoms/Button"
import { Input } from "design-system/atoms/Input"
import { Switch } from "design-system/atoms/Switch"
import { Textarea } from "design-system/atoms/Textarea"
import { Card, CardContent } from "design-system/molecules/Card"
import { MarkdownRenderer } from "design-system/organisms/chat/MarkdownRenderer"
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "design-system/organisms/Form"
import { getErrorTranslations } from "lib/i18n"
import { useEffect } from "react"
import { useForm } from "react-hook-form"
import { useTranslation } from "react-i18next"
import { Cron } from "react-js-cron"
import "react-js-cron/dist/styles.css"
import { BackofficeScheduledReportSchema, UpsertBackofficeScheduledReportSchema } from "schema"
import { z } from "zod"

type TypeUpsertBackofficeScheduledReportBody = z.input<typeof UpsertBackofficeScheduledReportSchema>

type BackofficeCustomerFormProps = {
  scheduledReport?: z.infer<typeof BackofficeScheduledReportSchema>
  onSubmit: (data: TypeUpsertBackofficeScheduledReportBody) => void
  submitCopy: string
  isPending: boolean
  error?: string | null
}

export const BackofficeScheduledReportForm = ({
  scheduledReport,
  onSubmit,
  submitCopy,
  isPending,
  error,
}: BackofficeCustomerFormProps) => {
  const { t } = useTranslation()
  const form = useForm<TypeUpsertBackofficeScheduledReportBody>({
    resolver: zodResolver(UpsertBackofficeScheduledReportSchema),
  })

  useEffect(() => {
    if (scheduledReport) {
      form.reset(scheduledReport)
    }
  }, [scheduledReport, form])

  const handleOnSubmit = (data: TypeUpsertBackofficeScheduledReportBody) => {
    onSubmit({ ...data })
  }

  return (
    <CardContent>
      <Form
        {...form}
        onSubmit={form.handleSubmit(handleOnSubmit)}
        className="flex flex-col gap-4"
        errorTranslations={getErrorTranslations()}
      >
        <div className="grid gap-4 grid-cols-2">
          <FormField
            control={form.control}
            name="alias"
            render={({ field }) => (
              <FormItem className="flex flex-col">
                <FormLabel>{t("backoffice.scheduledReports.form.alias")}</FormLabel>
                <FormControl>
                  <Input
                    {...field}
                    value={field.value?.toString()}
                    placeholder={t("backoffice.scheduledReports.form.alias")}
                    required
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="frequency"
            render={({ field }) => (
              <FormItem className="flex flex-col">
                <FormLabel>{t("backoffice.scheduledReports.form.frequency")}</FormLabel>
                <FormControl>
                  <Cron
                    className="text-xs! font-medium pl-2 [&_input]:h-[22px]! flex [&>div>div]:flex align-center [&>div>div]:align-center [&>div>div]:mb-0!"
                    value={field.value}
                    setValue={(value: string) => {
                      form.setValue(field.name, value)
                      form.trigger(field.name)
                    }}
                    allowClear={false}
                    clearButtonProps={{
                      className:
                        "bg-background! border-destructive! text-destructive! text-xs! py-1! h-auto! px-2 m-0! ml-2! hover:bg-red-100!",
                    }}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="spreadsheetId"
            render={({ field }) => (
              <FormItem className="flex flex-col">
                <FormLabel>{t("backoffice.scheduledReports.form.spreadsheetId")}</FormLabel>
                <FormControl>
                  <Input
                    {...field}
                    value={field.value?.toString()}
                    placeholder={t("backoffice.scheduledReports.form.spreadsheetId")}
                    required
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="cell"
            render={({ field }) => (
              <FormItem className="flex flex-col">
                <FormLabel>{t("backoffice.scheduledReports.form.cell")}</FormLabel>
                <FormControl>
                  <Input
                    {...field}
                    value={field.value?.toString()}
                    placeholder={t("backoffice.scheduledReports.form.cell")}
                    required
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="useFloatComma"
            render={({ field }) => (
              <FormItem className="flex gap-2 items-center">
                <FormControl>
                  <Switch
                    {...field}
                    value={field.value?.toString()}
                    checked={field.value}
                    onCheckedChange={(value) => {
                      form.setValue(field.name, value)
                      form.trigger(field.name)
                    }}
                  />
                </FormControl>
                <FormLabel>{t("backoffice.scheduledReports.form.useFloatComma")}</FormLabel>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>
        <div>
          <FormField
            control={form.control}
            name="query"
            render={({ field }) => (
              <FormItem className="flex flex-col">
                <FormLabel>{t("backoffice.scheduledReports.form.query")}</FormLabel>
                <FormControl>
                  <Textarea
                    {...field}
                    value={field.value?.toString()}
                    placeholder={t("backoffice.scheduledReports.form.query")}
                    required
                    rows={30}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>
        {error && (
          <Card className="p-3 bg-destructive/10 border-destructive/20 text-xs">
            <MarkdownRenderer>{error}</MarkdownRenderer>
          </Card>
        )}
        <div>
          <Button type="submit" disabled={!form.formState.isValid || isPending}>
            {submitCopy}
          </Button>
        </div>
      </Form>
    </CardContent>
  )
}
